import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Briefcase, Plus, Trash2, Users, X, Save, AlertCircle, Edit3, RefreshCw, ChevronRight, UserCircle } from 'lucide-react';
import { positionService, Position, PositionEmployee } from '../services';
import { uyar, hata, onayla } from '../components/GlobalModal';

const Positions = () => {
  const { t } = useTranslation();
  const [positions, setPositions] = useState<Position[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPosId, setEditingPosId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
    isActive: true
  });

  // Pozisyon calisanlari modal
  const [showEmployeesModal, setShowEmployeesModal] = useState(false);
  const [selectedPosition, setSelectedPosition] = useState<Position | null>(null);
  const [positionEmployees, setPositionEmployees] = useState<PositionEmployee[]>([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);

  // Pozisyonlari yukle
  const loadPositions = async () => {
    try {
      setLoading(true);
      const result = await positionService.getAll();
      if (result.success && result.data) {
        setPositions(result.data);
      }
    } catch (err) {
      console.error('Pozisyonlar yuklenirken hata:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPositions();
  }, []);

  const handleAdd = () => {
    setFormData({
      name: '',
      code: '',
      description: '',
      isActive: true
    });
    setEditingPosId(null);
    setError(null);
    setShowModal(true);
  };

  const handleEdit = (pos: Position) => {
    setFormData({
      name: pos.name,
      code: pos.code || '',
      description: pos.description || '',
      isActive: pos.isActive
    });
    setEditingPosId(pos.id);
    setError(null);
    setShowModal(true);
  };

  const handleSave = async () => {
    setError(null);
    if (!formData.name) {
      setError(t('positions.nameRequired'));
      return;
    }

    setSaving(true);
    try {
      if (editingPosId) {
        const result = await positionService.update(editingPosId, {
          name: formData.name,
          code: formData.code || undefined,
          description: formData.description || undefined,
          isActive: formData.isActive
        });
        if (result.success) {
          setShowModal(false);
          loadPositions();
        } else {
          setError(result.message || t('common.error'));
        }
      } else {
        const result = await positionService.create({
          name: formData.name,
          code: formData.code || undefined,
          description: formData.description || undefined
        });
        if (result.success) {
          setShowModal(false);
          loadPositions();
        } else {
          setError(result.message || t('common.error'));
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.message || t('common.error'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number, employeeCount: number) => {
    if (employeeCount > 0) {
      uyar(t('positions.cannotDeleteHasEmployees', { count: employeeCount }));
      return;
    }
    if (!(await onayla(t('positions.confirmDelete')))) return;

    try {
      const result = await positionService.delete(id);
      if (result.success) {
        loadPositions();
      } else {
        uyar(result.message || t('common.error'));
      }
    } catch (err: any) {
      uyar(err.response?.data?.message || t('common.error'));
    }
  };

  const handleShowEmployees = async (pos: Position) => {
    setSelectedPosition(pos);
    setShowEmployeesModal(true);
    setLoadingEmployees(true);
    try {
      const result = await positionService.getEmployees(pos.id);
      if (result.success && result.data) {
        setPositionEmployees(result.data);
      }
    } catch (err) {
      console.error('Calisanlar yuklenirken hata:', err);
    } finally {
      setLoadingEmployees(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="animate-spin text-blue-500" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 tracking-tight">{t('positions.title')}</h1>
          <p className="text-slate-500 text-sm">{t('positions.subtitle')}</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={loadPositions}
            className="bg-slate-100 text-slate-600 px-4 py-2.5 rounded-xl text-sm font-bold flex items-center gap-2 hover:bg-slate-200 transition-all"
          >
            <RefreshCw size={18} /> {t('common.refresh')}
          </button>
          <button
            onClick={handleAdd}
            className="bg-blue-600 text-white px-5 py-2.5 rounded-xl text-sm font-bold flex items-center gap-2 hover:bg-blue-700 transition-all shadow-lg shadow-blue-100 active:scale-95"
          >
            <Plus size={18} /> {t('positions.addPosition')}
          </button>
        </div>
      </div>

      {positions.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-12 text-center">
          <Briefcase className="mx-auto text-slate-300 mb-4" size={48} />
          <h3 className="text-lg font-bold text-slate-600">{t('positions.noPositions')}</h3>
          <p className="text-slate-400 text-sm mt-1">{t('positions.clickToAdd')}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {positions.map(pos => (
            <div key={pos.id} className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 flex flex-col group hover:border-blue-400 hover:shadow-xl hover:shadow-slate-100 transition-all duration-300">
              <div className="flex justify-between items-start mb-6">
                <div className="p-4 rounded-2xl bg-indigo-50 text-indigo-600 transition-transform group-hover:scale-110 duration-300 shadow-sm">
                  <Briefcase size={28} />
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={() => handleEdit(pos)}
                    className="text-slate-300 hover:text-blue-500 p-2 transition-colors hover:bg-blue-50 rounded-lg"
                    title={t('common.edit')}
                  >
                    <Edit3 size={18} />
                  </button>
                  <button
                    onClick={() => handleDelete(pos.id, pos.employeeCount)}
                    className="text-slate-300 hover:text-red-500 p-2 transition-colors hover:bg-red-50 rounded-lg"
                    title={t('common.delete')}
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>

              <div className="flex-1">
                <h3 className="text-xl font-bold text-slate-800 group-hover:text-indigo-600 transition-colors">{pos.name}</h3>
                {pos.code && (
                  <span className="text-xs font-mono text-slate-400 bg-slate-100 px-2 py-0.5 rounded mt-1 inline-block">{pos.code}</span>
                )}
                {pos.description && (
                  <p className="text-sm text-slate-400 mt-2">{pos.description}</p>
                )}
              </div>

              <div className="mt-8 pt-4 border-t border-slate-50 flex items-center justify-between">
                <span className={`text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-md ${pos.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                  {pos.isActive ? t('employees.active') : t('employees.inactive')}
                </span>
                <button
                  onClick={() => handleShowEmployees(pos)}
                  className="flex items-center gap-1 text-sm text-slate-500 hover:text-indigo-600 transition-colors"
                >
                  <Users size={16} />
                  <span className="font-bold">{pos.employeeCount}</span>
                  <span className="text-slate-400">{t('positions.employees')}</span>
                  <ChevronRight size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pozisyon Ekleme/Duzenleme Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4 backdrop-blur-md">
          <div className="bg-white rounded-[2rem] w-full max-w-lg shadow-2xl animate-in zoom-in-95 duration-200 overflow-hidden border border-slate-100 max-h-[90vh] overflow-y-auto">
            <div className="p-8 border-b border-slate-50 flex justify-between items-center bg-slate-50/50 sticky top-0">
              <div>
                <h2 className="text-2xl font-bold text-slate-800">{editingPosId ? t('positions.editPosition') : t('positions.addPosition')}</h2>
                <p className="text-sm text-slate-500 mt-1">{t('positions.enterDetails')}</p>
              </div>
              <button onClick={() => setShowModal(false)} className="text-slate-400 hover:text-slate-600 p-2 hover:bg-white rounded-full transition-all">
                <X size={24} />
              </button>
            </div>

            <div className="p-8 space-y-5">
              {error && (
                <div className="bg-red-50 border border-red-100 p-3 rounded-xl flex items-center gap-2 text-red-600 text-xs font-bold animate-in shake duration-300">
                  <AlertCircle size={16} />
                  {error}
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">{t('positions.positionName')} *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="w-full px-5 py-3.5 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 focus:outline-none text-sm font-bold transition-all"
                    placeholder="Yazilim Uzmani, Muhasebeci, vb."
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">{t('positions.positionCode')}</label>
                  <input
                    type="text"
                    value={formData.code}
                    onChange={(e) => setFormData({...formData, code: e.target.value})}
                    className="w-full px-5 py-3.5 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 focus:outline-none text-sm font-mono transition-all"
                    placeholder="POS001"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">{t('positions.description')}</label>
                  <textarea
                    rows={3}
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    className="w-full px-5 py-3.5 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 focus:outline-none text-sm transition-all resize-none font-medium"
                    placeholder={t('positions.descriptionPlaceholder')}
                  />
                </div>
                {editingPosId && (
                  <div className="flex items-center gap-3 pt-2">
                    <input
                      type="checkbox"
                      id="isActive"
                      checked={formData.isActive}
                      onChange={(e) => setFormData({...formData, isActive: e.target.checked})}
                      className="w-5 h-5 rounded-lg border-slate-300 text-blue-600 focus:ring-blue-500"
                    />
                    <label htmlFor="isActive" className="text-sm font-bold text-slate-600">{t('employees.active')}</label>
                  </div>
                )}
              </div>
            </div>

            <div className="p-8 bg-slate-50 border-t border-slate-50 flex flex-col gap-3 sticky bottom-0">
               <button
                onClick={handleSave}
                disabled={saving}
                className="w-full py-4 bg-blue-600 text-white rounded-2xl hover:bg-blue-700 transition-all shadow-xl shadow-blue-100 font-black text-sm uppercase tracking-widest flex items-center justify-center gap-2 active:scale-95 disabled:opacity-50"
               >
                 {saving ? <RefreshCw className="animate-spin" size={18} /> : <Save size={18} />}
                 {editingPosId ? t('common.update') : t('common.save')}
               </button>
               <button
                onClick={() => setShowModal(false)}
                className="w-full py-4 text-xs font-black uppercase tracking-widest text-slate-500 hover:text-slate-800 transition-colors"
               >
                 {t('common.cancel')}
               </button>
            </div>
          </div>
        </div>
      )}

      {/* Pozisyon Calisanlari Modal */}
      {showEmployeesModal && selectedPosition && (
        <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4 backdrop-blur-md">
          <div className="bg-white rounded-[2rem] w-full max-w-xl shadow-2xl animate-in zoom-in-95 duration-200 overflow-hidden border border-slate-100 max-h-[90vh] overflow-y-auto">
            <div className="p-8 border-b border-slate-50 flex justify-between items-center bg-slate-50/50 sticky top-0">
              <div>
                <h2 className="text-2xl font-bold text-slate-800">{selectedPosition.name}</h2>
                <p className="text-sm text-slate-500 mt-1">{positionEmployees.length} {t('positions.employees')}</p>
              </div>
              <button onClick={() => setShowEmployeesModal(false)} className="text-slate-400 hover:text-slate-600 p-2 hover:bg-white rounded-full transition-all">
                <X size={24} />
              </button>
            </div>

            <div className="p-8">
              {loadingEmployees ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="animate-spin text-blue-500" size={32} />
                </div>
              ) : positionEmployees.length === 0 ? (
                <div className="text-center py-12">
                  <Users className="mx-auto text-slate-300 mb-4" size={48} />
                  <p className="text-slate-400">{t('positions.noEmployeesInPosition')}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {positionEmployees.map(emp => (
                    <div key={emp.id} className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors">
                      <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center text-indigo-600">
                        <UserCircle size={24} />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-bold text-slate-800">{emp.fullName}</h4>
                        <p className="text-sm text-slate-500">{emp.departmentName}</p>
                      </div>
                      {emp.email && (
                        <span className="text-xs text-slate-400">{emp.email}</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Positions;
